import { useState, useEffect } from 'react';
import { COMMON_UI_UTILS, UIMarket } from '@drift/common';
import {
	BigNum,
	PERCENTAGE_PRECISION,
	PERCENTAGE_PRECISION_EXP,
	SpotMarketConfig,
	BN,
	ZERO,
} from '@drift-labs/sdk';
import { useDepositVault } from 'src/hooks/driftActions/vaults/useDepositVault';
import { useRequestVaultWithdrawal } from 'src/hooks/driftActions/vaults/useRequestVaultWithdrawal';
import { useCancelVaultWithdrawal } from 'src/hooks/driftActions/vaults/useCancelVaultWithdrawal';
import { useWithdrawFromVault } from 'src/hooks/driftActions/vaults/useWithdrawFromVault';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { vaultQueryKeys } from 'src/hooks/query/vaults/vaultQueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { Vault, VaultClient, VaultDepositor } from '@drift-labs/vaults-sdk';
import {
	getWithdrawalState,
	redeemPeriodToString,
	WithdrawalState,
} from 'src/utils/vault/withdraw';
import dayjs from 'dayjs';
import { VAULT_TERMS_AND_CONDITIONS_DO_NOT_SHOW_AGAIN_KEY } from 'src/constants/vaults/misc';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { getVaultDepositorBalance } from 'src/utils/vault/math';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import useTransferableWalletCollateralBalance from 'src/hooks/useTransferableWalletCollateralBalance';
import useModal from 'src/components/Modals/hooks/useModal';
import ToggleTabs from 'src/components/primitives/ToggleTabs/ToggleTabs';
import { ToggleTab } from 'src/components/primitives/ToggleTabs/types';
import VaultInput from './VaultInput';
import MarketIcon from 'src/components/Utils/MarketIcon';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { Hide } from '@drift-labs/icons';
import { UiVaultConfig } from 'src/@types/vaults';
import Button from 'src/components/primitives/Button/Button';
import InlineAlert, {
	InlineAlertVariant,
} from 'src/components/primitives/InlineAlert/InlineAlert';
import Tag from 'src/components/primitives/Tag/Tag';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

export interface VaultDepositWithdrawProps {
	type: 'deposit' | 'withdraw';
	uiVaultConfig: UiVaultConfig;
	vaultAccountData?: Vault;
	vaultDepositorAccountData?: VaultDepositor;
	isVaultDepositorLoaded: boolean;
	handleCloseForm?: () => void;
}

export const VaultDepositWithdrawForm = ({
	type,
	uiVaultConfig,
	vaultAccountData,
	vaultDepositorAccountData,
	isVaultDepositorLoaded,
	handleCloseForm,
}: VaultDepositWithdrawProps) => {
	const depositAssetConfig = UIMarket.createSpotMarket(
		uiVaultConfig.depositAsset
	).market as SpotMarketConfig;

	const { show } = useModal();
	const { captureEvent } = usePostHogCapture();
	const { depositVault } = useDepositVault();
	const { requestVaultWithdrawal } = useRequestVaultWithdrawal();
	const { cancelVaultWithdrawalRequest } = useCancelVaultWithdrawal();
	const { withdrawFromVault } = useWithdrawFromVault();
	const queryClient = useQueryClient();
	const { data: vaultStats } = useVaultStats(uiVaultConfig.vaultPubkeyString);
	const vaultClient = useVaultsStore((s) => s.vaultClient);
	const isWalletConnected = useWalletIsConnected();
	const authority = useCurrentAuthority();
	const syncVaultStats = () => {
		queryClient.invalidateQueries({
			queryKey: vaultQueryKeys.vaultOnChainData(),
		});
		queryClient.invalidateQueries({
			queryKey: vaultQueryKeys.vaultDepositorAccounts(authority?.toString()),
		});
		queryClient.invalidateQueries({
			queryKey: vaultQueryKeys.vaultDepositorHistories(authority?.toString()),
		});
	};

	const isDeprecating = uiVaultConfig.isDeprecating;
	const isDeprecated = uiVaultConfig.isDeprecated;
	const depositsDisabled = isDeprecating;

	const [formType, setFormType] = useState<'deposit' | 'withdraw'>(
		depositsDisabled ? 'withdraw' : type
	);

	const [depositAssetWalletBalance, isLoading] =
		useTransferableWalletCollateralBalance(depositAssetConfig);

	const hadSelectedDoNotShowAgain = useDriftStore(
		(s) => s[VAULT_TERMS_AND_CONDITIONS_DO_NOT_SHOW_AGAIN_KEY]
	);

	const [inputAmount, setInputAmount] = useState('');
	const [isClickedMaxWithdrawal, setIsClickedMaxWithdrawal] = useState(false);

	const isDeposit = formType === 'deposit';
	const redemptionPeriod = vaultAccountData?.redeemPeriod;

	const currentUserVaultBaseBalanceAfterFees = getCurrentUserVaultBalance();
	const inputAmountBigNum = BigNum.fromPrint(
		inputAmount,
		depositAssetConfig.precisionExp
	);
	const afterInputAmountUserVaultBalance =
		getAfterInputAmountUserVaultBalance();
	const maxAmount = getMaxAmount();
	const exceededMax = inputAmountBigNum.gt(maxAmount);
	const { withdrawalState } = getWithdrawalState(
		vaultDepositorAccountData,
		vaultAccountData
	);

	const isVaultDepositorWhitelisted = getIsVaultDepositorWhitelisted();
	const isInputAmountLessThanMinDepositAmount =
		getIsInputAmountLessThanMinDepositAmount();
	const isCtaDisabled = getIsCtaDisabled();
	const withdrawalCtaState = getWithdrawalFormState();
	const withdrawalBaseAmount = getWithdrawalBaseAmount();
	const tsUntilWithdrawal = getTsUntilWithdrawal();
	const alertDetails = getAlertDetails();

	useEffect(() => {
		if (exceededMax && isWalletConnected) {
			setInputAmount(maxAmount.print());
		}
	}, [exceededMax, isWalletConnected, maxAmount]);

	const formOptions: ToggleTab<'deposit' | 'withdraw'>[] = [
		{
			label: 'Deposit',
			value: 'deposit',
			disabled: depositsDisabled,
		},
		{
			label: 'Withdraw',
			value: 'withdraw',
		},
	];

	function getIsInputAmountLessThanMinDepositAmount() {
		return (
			vaultAccountData?.minDepositAmount.gtn(0) &&
			!isNaN(+inputAmount) &&
			+inputAmount > 0 &&
			BigNum.fromPrint(inputAmount, depositAssetConfig.precisionExp).val.lt(
				vaultAccountData.minDepositAmount
			)
		);
	}

	function getIsVaultDepositorWhitelisted() {
		if (!vaultAccountData?.permissioned) return true;

		if (isVaultDepositorLoaded && !vaultDepositorAccountData) {
			return false;
		}

		return true;
	}

	function getCurrentUserVaultBalance() {
		if (!vaultAccountData || !vaultDepositorAccountData || !vaultStats) {
			return BigNum.from(0, depositAssetConfig.precisionExp);
		}

		return getVaultDepositorBalance(
			vaultDepositorAccountData,
			vaultAccountData,
			vaultStats.tvlBase,
			depositAssetConfig.precisionExp,
			true
		);
	}

	function getMaxAmount() {
		if (isDeposit) {
			if (!vaultAccountData || !vaultStats)
				return BigNum.from(0, depositAssetConfig.precisionExp);

			// uncapped vault capacity
			if (vaultAccountData.maxTokens.eq(ZERO)) {
				return depositAssetWalletBalance;
			}

			// Note :: The smart contract implicitly reduces the deposit amount if it is larger than the max capacity so we don't need to do any handling for that here
			const maxCapacity = vaultAccountData.maxTokens;

			const capacityRemaining = BN.max(
				maxCapacity.sub(vaultStats.tvlBase.val),
				ZERO
			);

			const maxAmount = BN.min(
				capacityRemaining,
				depositAssetWalletBalance.val
			);
			const safeMaxAmount = BN.max(maxAmount, ZERO);

			return BigNum.from(safeMaxAmount, depositAssetConfig.precisionExp);
		} else {
			return currentUserVaultBaseBalanceAfterFees;
		}
	}

	function getAfterInputAmountUserVaultBalance() {
		if (isDeposit) {
			return currentUserVaultBaseBalanceAfterFees.add(inputAmountBigNum);
		} else {
			return currentUserVaultBaseBalanceAfterFees.sub(inputAmountBigNum);
		}
	}

	function getWithdrawalFormState() {
		if (withdrawalState === WithdrawalState.UnRequested) {
			return {
				text: 'Request Withdrawal',
				className: 'bg-negative-red',
				displayInput: true,
			};
		} else if (withdrawalState === WithdrawalState.Requested) {
			return {
				text: 'Cancel Request',
				className: 'bg-button-secondary-bg text-text-default',
				displayInput: false,
			};
		} else {
			return {
				text: 'Confirm Withdrawal',
				className: 'bg-negative-red',
				displayInput: false,
			};
		}
	}

	/**
	 * The amount of base value that can be withdrawn is the smaller of the current base value of the request
	 * or the base value of the withdrawal at the time of the request.
	 */
	function getWithdrawalBaseAmount() {
		const zeroBaseBigNum = BigNum.from(0, depositAssetConfig.precisionExp);

		if (withdrawalState === WithdrawalState.UnRequested) {
			return zeroBaseBigNum;
		} else {
			if (!vaultAccountData || !vaultStats || !vaultDepositorAccountData) {
				return zeroBaseBigNum;
			}

			const totalShares = vaultAccountData.totalShares;
			const currentBaseValueOfRequest =
				vaultDepositorAccountData.lastWithdrawRequest.shares
					.mul(vaultStats.tvlBase.val)
					.div(totalShares);

			const baseValueOfWithdrawalAtRequestTime = new BN(
				vaultDepositorAccountData.lastWithdrawRequest.value
			);

			const smallerValue = BN.min(
				baseValueOfWithdrawalAtRequestTime,
				currentBaseValueOfRequest
			);

			return BigNum.from(smallerValue, depositAssetConfig.precisionExp);
		}
	}

	function getTsUntilWithdrawal() {
		const withdrawalAvailableTs =
			(vaultDepositorAccountData?.lastWithdrawRequest.ts.toNumber() ?? 0) +
			(vaultAccountData?.redeemPeriod.toNumber() ?? 0) -
			dayjs().unix();
		return withdrawalAvailableTs;
	}

	function getIsCtaDisabled() {
		if (!isVaultDepositorLoaded) return true;

		if (!isVaultDepositorWhitelisted) return true;

		if (
			withdrawalState === WithdrawalState.UnRequested &&
			formType === 'deposit' &&
			isInputAmountLessThanMinDepositAmount
		) {
			return true;
		}

		if (
			withdrawalState === WithdrawalState.Requested ||
			withdrawalState === WithdrawalState.AvailableForWithdrawal
		)
			return formType === 'deposit';

		if (+inputAmount === 0) return true;

		return false;
	}

	function getAlertDetails(): {
		type: InlineAlertVariant;
		message: string;
	} | null {
		if (!isVaultDepositorWhitelisted && isVaultDepositorLoaded) {
			return {
				type: 'error',
				message: 'You are not whitelisted for this vault',
			};
		}

		if (isDeposit) {
			if (withdrawalState !== WithdrawalState.UnRequested) {
				return {
					type: 'warning',
					message: `Deposits are disabled while a withdrawal request is in progress`,
				};
			}
		} else {
			if (withdrawalState === WithdrawalState.Requested) {
				return {
					type: 'info',
					message: `${withdrawalBaseAmount.prettyPrint()} ${
						depositAssetConfig.symbol
					} is available to withdraw in ${redeemPeriodToString(
						tsUntilWithdrawal
					)}.`,
				};
			} else if (withdrawalState === WithdrawalState.AvailableForWithdrawal) {
				return {
					type: 'success',
					message: `${withdrawalBaseAmount.prettyPrint()} ${
						depositAssetConfig.symbol
					} is available for withdrawal`,
				};
			}
		}

		return null;
	}

	const handleOnValueChange = (value?: string) => {
		if (!isDeposit) {
			setIsClickedMaxWithdrawal(
				BigNum.fromPrint(value ?? '', depositAssetConfig.precisionExp).eq(
					maxAmount
				)
			);
		} else {
			setIsClickedMaxWithdrawal(false);
		}
		COMMON_UI_UTILS.formatTokenInputCurried(
			setInputAmount,
			depositAssetConfig
		)(value ?? '');
	};

	const handleOnSubmit = async () => {
		if (isCtaDisabled) return;

		const actionCallback = async () => {
			if (isDeposit) {
				await depositVault(
					vaultAccountData as Vault,
					vaultClient as VaultClient,
					BigNum.fromPrint(inputAmount, depositAssetConfig.precisionExp).val,
					vaultDepositorAccountData
				);

				captureEvent('deposit_strat_vault');
			} else {
				if (!vaultDepositorAccountData) return;

				if (withdrawalState === WithdrawalState.UnRequested) {
					const inputAmountBN = BigNum.fromPrint(
						inputAmount,
						depositAssetConfig.precisionExp
					);
					const isMaxWithdrawal =
						inputAmountBN.eq(currentUserVaultBaseBalanceAfterFees) ||
						isClickedMaxWithdrawal;
					const pctOfSharesToWithdraw = isMaxWithdrawal
						? PERCENTAGE_PRECISION
						: inputAmountBN
								.shift(PERCENTAGE_PRECISION_EXP)
								.div(currentUserVaultBaseBalanceAfterFees).val;

					await requestVaultWithdrawal(
						vaultDepositorAccountData.pubkey,
						pctOfSharesToWithdraw,
						vaultClient as VaultClient
					);
				} else if (withdrawalState === WithdrawalState.Requested) {
					await cancelVaultWithdrawalRequest(
						vaultDepositorAccountData.pubkey,
						vaultClient as VaultClient
					);
				} else {
					// Event needs to be captured before `withdrawFromVault` is called,
					// otherwise `lastWithdrawRequest.value` will be reset. While it's
					// possible the user may cancel or the tx may otherwise fail, it's
					// fine to simply capture it regardless.
					const amount = BigNum.from(
						vaultDepositorAccountData.lastWithdrawRequest.value,
						depositAssetConfig.precisionExp
					).toNum();

					captureEvent('withdraw_strat_vault', { amount });

					await withdrawFromVault(
						vaultDepositorAccountData,
						vaultClient as VaultClient
					);
				}
			}

			setInputAmount('');

			// allow time for action to be processed on the blockchain
			setTimeout(() => {
				syncVaultStats();
			}, 3000);
		};

		if (!hadSelectedDoNotShowAgain) {
			show({
				type: 'vault_terms_disclaimer',
				props: {
					depositCallback: actionCallback,
				},
			});
			return;
		}

		actionCallback();
	};

	return (
		<div
			className="sm-grow-0 flex w-full grow flex-col rounded-sm bg-container-bg p-4"
			data-testid="vault-deposit-withdraw-form"
		>
			<div className="flex w-full flex-row gap-x-md">
				<ToggleTabs
					options={formOptions}
					activeTab={formType}
					onTabChange={setFormType}
					animatedBackgroundClassName="bg-button-primary-bg"
					dataTestId="vault-form-toggle"
				/>
				{handleCloseForm && (
					<IconButton
						icon={Hide}
						size="lg"
						className="size-[50px]"
						onClick={handleCloseForm}
					/>
				)}
			</div>

			{isDeprecating && (
				<Tag className="bg-warn-orange-secondary-bg text-star-yellow">
					Deposits are disabled because the vault is{' '}
					{isDeprecated ? 'deprecated' : 'soon to be deprecated'}. Please
					withdraw your funds.
				</Tag>
			)}

			<div className="mt-4 flex flex-col gap-4">
				<VaultInput
					isDeposit={isDeposit}
					withdrawalCtaState={withdrawalCtaState}
					maxAmount={maxAmount}
					depositAssetConfig={depositAssetConfig}
					handleOnValueChange={handleOnValueChange}
					inputAmount={inputAmount}
					isLoading={isLoading}
					setIsClickedMaxWithdrawal={setIsClickedMaxWithdrawal}
					redemptionPeriod={redemptionPeriod}
					minDepositAmount={vaultAccountData?.minDepositAmount}
				/>
			</div>

			{/** Alerts */}
			<div className="mt-2 mb-5 flex w-full grow items-end sm:h-[100px] sm:grow-0">
				{alertDetails && (
					<InlineAlert
						className="w-full"
						variant={alertDetails.type}
						title={alertDetails.message}
					/>
				)}
			</div>

			<div className="flex w-full justify-between">
				<span className="typo-b4 text-text-secondary">Net Asset Balance</span>
				<span className="flex items-center gap-xs typo-b4 text-text-default">
					<MarketIcon
						marketSymbol={depositAssetConfig.symbol}
						className="size-4"
					/>
					{depositAssetConfig.symbol === 'USDC'
						? afterInputAmountUserVaultBalance.toFixed(2)
						: afterInputAmountUserVaultBalance.prettyPrint()}
				</span>
			</div>

			{isWalletConnected ? (
				<Button
					size="lg"
					variant="primary"
					className="mt-xl w-full"
					onClick={handleOnSubmit}
					disabled={isCtaDisabled}
					dataTestId={
						isDeposit ? 'vault-deposit-submit' : 'vault-withdraw-submit'
					}
				>
					{isDeposit ? 'Confirm Deposit' : withdrawalCtaState.text}
				</Button>
			) : (
				<Button
					size="lg"
					variant="primary"
					className="mt-xl w-full"
					onClick={() => show({ type: 'connect_wallet', props: {} })}
					dataTestId="vault-connect-wallet"
				>
					Connect Wallet
				</Button>
			)}
		</div>
	);
};

export const VaultDepositWithdrawFormWithGradientWrapper = (
	props: VaultDepositWithdrawProps
) => {
	return (
		<div className="w-full rounded-sm p-px">
			<VaultDepositWithdrawForm {...props} />
		</div>
	);
};
